import type { FloorContentDM } from '@Model/FloorModels';
import { injectable } from 'inversify';

import { GetFloor } from './ownable.dtos';
import type {
  FloorContentCellDTO,
  FloorContentDTO,
  GetFloorResponse,
} from './ownable.dtos';
import ServiceStackGateway from './service-stack';

import container from '~ioc/ioc';
import type { JourneyParameters } from '~types/navigation';

@injectable()
export class FloorGateway {
  getFloor = async (id?: number): Promise<FloorContentDM> => {
    if (!id) {
      throw new Error('Floor id is not provided for fetching');
    }

    const request = new GetFloor();
    request.floorId = id;

    const response = await ServiceStackGateway.get<GetFloorResponse>(request);
    if (!response.success) {
      throw new Error(
        response.errors?.[0].userMessage ??
          `Unknown Error fetching Floor with id ${id}`
      );
    }

    return convertFloorDtoToFloorDM(response.content);
  };
}

container.bind(FloorGateway).toSelf().inTransientScope();

const convertFloorDtoToFloorDM = (dto: FloorContentDTO): FloorContentDM => {
  const layout: FloorContentDM['layout'] =
    dto.displayType === 'Unspecified' ? 'BrandConcession' : dto.displayType;

  return {
    ...dto,
    layout,
    cells: dto.cells.map(destinationAdder(layout)),
  };
};

// replaces the original Destination JourneyParam (now deprecated) with the DisplayId
const destinationAdder =
  (layout: FloorContentDTO['displayType']) =>
  (cell: FloorContentDTO['cells'][0]) => ({
    ...cell,
    destination: getDestination(cell, layout),
  });

function getDestination(
  cell: FloorContentCellDTO,
  layout: FloorContentDTO['displayType']
): JourneyParameters {
  return {
    journey: layout.toLowerCase() as JourneyParameters['journey'],
    journeyParam: cell.displayId,
  };
}
